/**
 * Configuración de billing — markup global editable (Fase 34).
 *
 * El bps cambiado se aplica a sesiones / transacciones NUEVAS. Las históricas
 * (credit_transactions + spans) conservan el `markup_bps` que tenían al ser
 * persistidas, así que los reportes de revenue/margin de períodos pasados
 * siguen siendo exactos.
 *
 * Patrón heredado de `Routing/Models.tsx` (Fase 33).
 */
import { useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchBillingConfig,
  fetchBillingConfigAudit,
  patchBillingConfig,
  type BillingConfig,
  type BillingConfigAuditEntry,
} from '../../api/admin/billing-config.js';
import { AdminApiError } from '../../api/admin/_core.js';

export function BillingConfigPage(): ReactElement {
  const [tab, setTab] = useState<'edit' | 'audit'>('edit');
  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Billing · Configuración</h1>
          <p className="page-sub">
            Margen aplicado sobre el costo real de proveedores. Cambios afectan
            sesiones nuevas — las históricas mantienen su markup persistido.
          </p>
        </div>
      </div>
      <div className="config-page">
        <section className="card">
          <header className="card-head config-tabs">
            <div className="segmented" role="tablist" aria-label="Vista">
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'edit'}
                className={tab === 'edit' ? 'is-active' : ''}
                onClick={() => setTab('edit')}
              >
                Editar
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'audit'}
                className={tab === 'audit' ? 'is-active' : ''}
                onClick={() => setTab('audit')}
              >
                Historial
              </button>
            </div>
            <small>
              Cambios afectan <strong>sesiones nuevas</strong> · históricas
              conservan su markup persistido por fila
            </small>
          </header>
          {tab === 'edit' ? <BillingEditor /> : <BillingAudit />}
        </section>
      </div>
    </>
  );
}

function BillingEditor(): ReactElement {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['admin', 'billing-config'],
    queryFn: fetchBillingConfig,
  });
  const [draft, setDraft] = useState<BillingConfig | null>(null);
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const mutation = useMutation({
    mutationFn: (next: BillingConfig) => patchBillingConfig(next),
    onSuccess: () => {
      setDraft(null);
      setToast({ kind: 'ok', text: 'Configuración guardada' });
      void qc.invalidateQueries({ queryKey: ['admin', 'billing-config'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'billing-config-audit'] });
    },
    onError: (err: unknown) => {
      const msg = err instanceof AdminApiError ? err.message : String(err);
      setToast({ kind: 'err', text: msg });
    },
  });

  if (query.isPending) return <div className="config-state">Cargando…</div>;
  if (query.isError) {
    return (
      <div className="config-state config-state-err">
        Error: {(query.error).message}
      </div>
    );
  }
  if (!query.data) return <div className="config-state">Sin datos</div>;

  const data = query.data;
  const effective: BillingConfig = draft ?? data.config;
  const isDirty = draft !== null;

  const setMarkupBps = (next: number): void => {
    setDraft({ ...effective, markupBps: next });
  };

  return (
    <div className="config-editor">
      <div className="config-meta">
        <span className="config-meta-label">Última edición</span>
        <span className="config-meta-value mono">
          {data.updatedAt
            ? new Date(data.updatedAt).toLocaleString('es-AR', {
                dateStyle: 'medium',
                timeStyle: 'short',
              })
            : 'nunca · usando defaults'}
        </span>
      </div>

      <div className="config-field">
        <label className="config-field-label" htmlFor="markup-bps">
          Markup (basis points)
        </label>
        <div className="config-field-row">
          <input
            id="markup-bps"
            type="number"
            min={0}
            max={100000}
            step={50}
            className="config-input"
            value={effective.markupBps}
            onChange={(e) => {
              const v = Number.parseInt(e.target.value, 10);
              if (Number.isFinite(v)) setMarkupBps(v);
            }}
            disabled={mutation.isPending}
          />
          <span className="config-field-suffix mono">
            = {(effective.markupBps / 100).toFixed(2)}%
          </span>
        </div>
        <p className="config-field-hint">
          10000 bps = 100% markup (precio final = costo proveedor × 2).
          <br />
          Default: 2000 bps (20%). Rango permitido: 0 – 100000.
        </p>
      </div>

      <footer className="config-footer">
        <div className="config-footer-left">
          {isDirty && (
            <span className="config-dirty-pill">
              <span className="config-dirty-dot" aria-hidden="true" />
              Hay cambios sin guardar
            </span>
          )}
          {toast && (
            <span
              className={toast.kind === 'ok' ? 'config-toast ok' : 'config-toast err'}
              role="status"
            >
              {toast.text}
            </span>
          )}
        </div>
        <div className="config-footer-actions">
          {isDirty && (
            <button
              type="button"
              className="btn ghost"
              onClick={() => setDraft(null)}
              disabled={mutation.isPending}
            >
              Descartar
            </button>
          )}
          <button
            type="button"
            className="btn primary"
            disabled={!isDirty || mutation.isPending}
            onClick={() => draft && mutation.mutate(draft)}
          >
            {mutation.isPending ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </footer>
    </div>
  );
}

function BillingAudit(): ReactElement {
  const query = useQuery({
    queryKey: ['admin', 'billing-config-audit'],
    queryFn: fetchBillingConfigAudit,
  });
  if (query.isPending) return <div className="config-state">Cargando…</div>;
  if (query.isError) {
    return (
      <div className="config-state config-state-err">
        Error: {(query.error).message}
      </div>
    );
  }
  const entries = query.data?.entries ?? [];
  if (entries.length === 0) {
    return (
      <div className="config-state">
        Sin cambios registrados. El primer cambio del markup va a aparecer acá.
      </div>
    );
  }
  return (
    <div className="config-audit">
      {entries.map((entry) => (
        <BillingAuditCard key={entry.id} entry={entry} />
      ))}
    </div>
  );
}

function BillingAuditCard({ entry }: { entry: BillingConfigAuditEntry }): ReactElement {
  const when = new Date(entry.changedAt).toLocaleString('es-AR', {
    dateStyle: 'medium',
    timeStyle: 'medium',
  });
  return (
    <article className="audit-entry">
      <header className="audit-entry-head">
        <span className="audit-entry-when">{when}</span>
        <span className="audit-entry-by mono">
          por <code>{entry.changedByUserId?.slice(0, 8) ?? '—'}</code>
        </span>
      </header>
      <div className="audit-entry-diff">
        <div className="audit-side">
          <span className="audit-side-label">Antes</span>
          <pre className="audit-json">
            {entry.beforeConfig
              ? JSON.stringify(entry.beforeConfig, null, 2)
              : '— (sin row previa)'}
          </pre>
        </div>
        <div className="audit-side">
          <span className="audit-side-label">Después</span>
          <pre className="audit-json">{JSON.stringify(entry.afterConfig, null, 2)}</pre>
        </div>
      </div>
    </article>
  );
}
